// src/services/facilityService.js - Enhanced for UserManagement compatibility
import { apiService } from "./api";

export const facilityService = {
  // Get all facilities (ENHANCED: better error handling and fallback)
  getFacilities: async (skip = 0, limit = 100) => {
    try {
      const response = await apiService.get("/facilities", {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      console.warn("Error fetching facilities:", error.message);

      // Enhanced fallback data for development
      if (error.response?.status === 404 || error.response?.status === 500) {
        return [
          {
            id: 1,
            company_name: "Main Medical Center",
            phone_number: "(555) 123-4567",
            address: "123 Main St, Los Angeles, CA 90001",
            email: "contact@mainmedical.com",
            subscription_type: "premium",
            admin_level: 3,
            business_license: "BL123456",
            npi: "1234567890",
            users: [],
            medical_professionals: [],
            orders: [],
            created_at: new Date().toISOString(),
          },
          {
            id: 2,
            company_name: "Community Health Clinic",
            phone_number: "(555) 987-6543",
            address: "456 Oak Ave, Beverly Hills, CA 90210",
            email: "info@communityclinic.com",
            subscription_type: "standard",
            admin_level: 2,
            business_license: "BL654321",
            npi: "0987654321",
            users: [],
            medical_professionals: [],
            orders: [],
            created_at: new Date().toISOString(),
          },
        ];
      }
      throw error;
    }
  },

  // Get facility by ID
  getFacilityById: async (facilityId) => {
    try {
      const response = await apiService.get(`/facilities/${facilityId}`);
      return response.data;
    } catch (error) {
      console.warn(`Error fetching facility ${facilityId}:`, error.message);

      // Fallback for development
      if (error.response?.status === 404) {
        return {
          id: facilityId,
          company_name: "Sample Medical Center",
          phone_number: "(555) 123-4567",
          address: "123 Main St, Los Angeles, CA 90001",
          email: "contact@sample.com",
          subscription_type: "standard",
          admin_level: 2,
          business_license: "BL123456",
          npi: "1234567890",
          users: [],
          medical_professionals: [],
          orders: [],
          created_at: new Date().toISOString(),
        };
      }
      throw error;
    }
  },

  // Create new facility
  createFacility: async (facilityData) => {
    try {
      const response = await apiService.post("/facilities", facilityData);
      return response.data;
    } catch (error) {
      console.error("Error creating facility:", error);

      // Enhanced error handling
      if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("already exists")) {
          throw new Error("A facility with this name or email already exists");
        } else if (detail?.includes("Invalid subscription")) {
          throw new Error("Invalid subscription type selected");
        }
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to create facilities");
      }

      throw error;
    }
  },

  // Update facility
  updateFacility: async (facilityId, facilityData) => {
    try {
      const response = await apiService.put(`/facilities/${facilityId}`, facilityData);
      return response.data;
    } catch (error) {
      console.error(`Error updating facility ${facilityId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Facility not found");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to update this facility");
      }

      throw error;
    }
  },

  // Delete facility
  deleteFacility: async (facilityId) => {
    try {
      const response = await apiService.delete(`/facilities/${facilityId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting facility ${facilityId}:`, error);

      if (error.response?.status === 404) {
        throw new Error("Facility not found");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to delete this facility");
      } else if (error.response?.status === 409) {
        throw new Error("Cannot delete facility with active users or orders");
      }

      // Development fallback
      if (error.response?.status === 501 || error.response?.status === 405) {
        console.warn("Delete endpoint not implemented yet, simulating success");
        return {
          message: "Facility deleted successfully (simulated)",
          id: facilityId,
        };
      }

      throw error;
    }
  },

  // Get facility statistics
  getFacilityStats: async (facilityId) => {
    try {
      const response = await apiService.get(`/facilities/${facilityId}/stats`);
      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching stats for facility ${facilityId}:`,
        error.message
      );

      // Return fallback stats
      return {
        total_users: 0,
        total_medical_professionals: 0,
        total_orders: 0,
        active_orders: 0,
        completed_orders: 0,
        monthly_orders: 0,
      };
    }
  },

  // Get facility users
  getFacilityUsers: async (facilityId, skip = 0, limit = 100) => {
    try {
      const response = await apiService.get(`/facilities/${facilityId}/users`, {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching users for facility ${facilityId}:`,
        error.message
      );
      return [];
    }
  },

  // Get facility medical professionals
  getFacilityMedicalProfessionals: async (
    facilityId,
    skip = 0,
    limit = 100
  ) => {
    try {
      const response = await apiService.get(
        `/facilities/${facilityId}/medical-professionals`,
        {
          params: { skip, limit },
        }
      );
      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching medical professionals for facility ${facilityId}:`,
        error.message
      );

      // Try the general medical professionals endpoint with facility filter
      try {
        const response = await apiService.get("/medical-professionals", {
          params: { facility_id: facilityId, skip, limit },
        });
        return response.data;
      } catch (innerError) {
        return [];
      }
    }
  },

  // Get facility orders
  getFacilityOrders: async (facilityId, params = {}) => {
    try {
      const response = await apiService.get(`/facilities/${facilityId}/orders`, {
        params: { ...params },
      });
      return response.data;
    } catch (error) {
      console.warn(
        `Error fetching orders for facility ${facilityId}:`,
        error.message
      );

      // Try the general orders endpoint with facility filter
      try {
        const response = await apiService.get("/orders", {
          params: { facility_id: facilityId, ...params },
        });
        return response.data;
      } catch (innerError) {
        return [];
      }
    }
  },

  // Update facility subscription
  updateFacilitySubscription: async (facilityId, subscriptionData) => {
    try {
      const response = await apiService.put(
        `/facilities/${facilityId}/subscription`,
        subscriptionData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating subscription for facility ${facilityId}:`,
        error
      );
      throw error;
    }
  },

  // Get facility subscription types for dropdown
  getSubscriptionTypes: () => {
    return [
      { value: "basic", label: "Basic" },
      { value: "standard", label: "Standard" },
      { value: "premium", label: "Premium" },
    ];
  },

  // Search facilities (for UserManagement facility dropdown)
  searchFacilities: async (query) => {
    try {
      const response = await apiService.get("/facilities/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.warn("Error searching facilities:", error.message);

      // Fallback to getFacilities and filter locally
      const allFacilities = await facilityService.getFacilities();
      return allFacilities.filter(
        (facility) =>
          facility.company_name?.toLowerCase().includes(query.toLowerCase()) ||
          facility.email?.toLowerCase().includes(query.toLowerCase())
      );
    }
  },

  // Validate facility data (for UserManagement form validation)
  validateFacilityData: (facilityData) => {
    const errors = {};

    if (!facilityData.company_name?.trim()) {
      errors.company_name = "Company name is required";
    }

    if (!facilityData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(facilityData.email)) {
      errors.email = "Invalid email format";
    }

    if (!facilityData.phone_number?.trim()) {
      errors.phone_number = "Phone number is required";
    }

    if (!facilityData.address?.trim()) {
      errors.address = "Address is required";
    }

    if (!facilityData.subscription_type) {
      errors.subscription_type = "Subscription type is required";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Get facilities for dropdown (simplified for UserManagement)
  getFacilitiesForDropdown: async () => {
    try {
      const facilities = await facilityService.getFacilities(0, 1000); // Get all facilities
      return facilities.map((facility) => ({
        id: facility.id,
        company_name: facility.company_name,
        subscription_type: facility.subscription_type,
        admin_level: facility.admin_level,
      }));
    } catch (error) {
      console.warn("Error fetching facilities for dropdown:", error.message);
      return [];
    }
  },

  // Check if user can access facility (for UserManagement permission checks)
  canAccessFacility: (facilityId, userRole, userFacilityId) => {
    if (userRole === "super_admin") {
      return true;
    }

    if (["admin", "facility_admin"].includes(userRole)) {
      return userFacilityId === facilityId;
    }

    return false;
  },

  // Get facility name by ID (utility for UserManagement display)
  getFacilityName: async (facilityId) => {
    try {
      if (!facilityId) return "No Facility";

      const facility = await facilityService.getFacilityById(facilityId);
      return facility?.company_name || "Unknown Facility";
    } catch (error) {
      console.warn(
        `Error getting facility name for ID ${facilityId}:`,
        error.message
      );
      return "Unknown Facility";
    }
  },
};
